import { useLazyQuery } from '@apollo/client/react'
import { Button, ErrorState, LoadingState, useTranslate } from '@athena/ui'
import { useEffect, useRef, useState } from 'react'

import { AdminAuditLogDocument } from '../../graphql/generated/operations.js'
import { AuditTrail, type AuditEntry } from '../subscription/AuditTrail.js'

const PAGE_SIZE = 25

export interface ResourceAuditLogProps {
  resourceType: string
  resourceId: string
}

/** A bounded, resource-scoped audit history for Admin detail pages. */
export function ResourceAuditLog({ resourceType, resourceId }: ResourceAuditLogProps) {
  const t = useTranslate()
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [hasMore, setHasMore] = useState(false)
  const firstRequest = useRef(true)
  const [load, { loading, error }] = useLazyQuery(AdminAuditLogDocument)

  useEffect(() => {
    firstRequest.current = true
    setEntries([])
    setHasMore(false)
    void load({
      variables: { subjectType: resourceType, subjectId: resourceId, offset: 0, limit: PAGE_SIZE },
    }).then((result) => {
      const next = result.data?.adminAuditLog ?? []
      setEntries(next)
      setHasMore(next.length === PAGE_SIZE)
      firstRequest.current = false
    })
  }, [load, resourceId, resourceType])

  async function loadMore() {
    const result = await load({
      variables: {
        subjectType: resourceType,
        subjectId: resourceId,
        offset: entries.length,
        limit: PAGE_SIZE,
      },
    })
    const next = result.data?.adminAuditLog ?? []
    setEntries((current) => [...current, ...next])
    setHasMore(next.length === PAGE_SIZE)
  }

  if (loading && firstRequest.current) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error && entries.length === 0) return <ErrorState>{t('admin.loadError')}</ErrorState>

  return (
    <div className="flex flex-col gap-4">
      <AuditTrail entries={entries} />
      {hasMore ? (
        <Button
          type="button"
          variant="secondary"
          className="self-start"
          disabled={loading}
          onClick={() => void loadMore()}
        >
          {loading ? t('admin.auditLoadingMore') : t('admin.auditLoadMore')}
        </Button>
      ) : null}
      {error && entries.length > 0 ? (
        <p role="alert" className="text-body-sm text-error-text">
          {t('admin.loadError')}
        </p>
      ) : null}
    </div>
  )
}
